import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, User, Tag } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return dateStr; }
}

function renderContent(content) {
  if (!content) return null;
  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      elements.push(<h2 key={i} className="font-['Outfit'] text-2xl font-bold text-[#111827] mt-8 mb-3">{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      elements.push(<h3 key={i} className="font-['Outfit'] text-xl font-bold text-[#111827] mt-6 mb-2">{line.slice(4)}</h3>);
    } else if (line.match(/^\d+\.\s+\*\*/)) {
      const match = line.match(/^(\d+)\.\s+\*\*(.+?)\*\*\s*(.*)/);
      if (match) {
        elements.push(
          <div key={i} className="mt-6 mb-2">
            <h3 className="font-['Outfit'] text-lg font-bold text-[#111827]">
              <span className="text-[#06B6D4] mr-2">{match[1]}.</span>{match[2]}
            </h3>
            {match[3] && <p className="text-base text-[#6B7280] leading-relaxed mt-1">{match[3]}</p>}
          </div>
        );
      } else {
        elements.push(<p key={i} className="text-base text-[#6B7280] leading-relaxed mb-3">{line}</p>);
      }
    } else if (line.startsWith("**") && line.endsWith("**")) {
      elements.push(<p key={i} className="font-semibold text-[#111827] mt-4 mb-1">{line.slice(2, -2)}</p>);
    } else if (line.startsWith("#")) {
      const tagLine = line.split(" ").filter(t => t.startsWith("#"));
      elements.push(
        <div key={i} className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-[#E5E7EB]">
          {tagLine.map((tag, ti) => (
            <span key={ti} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#06B6D4]/10 text-[#06B6D4] border border-[#06B6D4]/20">
              {tag}
            </span>
          ))}
        </div>
      );
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-base text-[#6B7280] leading-relaxed mb-3">{line}</p>);
    }
    i++;
  }
  return elements;
}

export default function BlogPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/blog/${postId}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setPost)
      .catch(() => navigate("/"))
      .finally(() => setLoading(false));
  }, [postId, navigate]);

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <p className="text-[#6B7280]">Loading...</p>
    </div>
  );

  if (!post) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="blog-post-page">
      <nav className="sticky top-0 z-50 glass-nav border-b border-[#E5E7EB] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-medium text-[#6B7280] hover:text-[#111827] transition-colors" data-testid="blog-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Portfolio
            </button>
          </div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#06B6D4]/10 text-[#06B6D4] border border-[#06B6D4]/20 mb-4">
            {post.category}
          </span>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#111827] leading-tight mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
            <span className="inline-flex items-center gap-1.5">
              <User className="w-4 h-4" /> {post.author || "Piyus Kumar"}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4" /> {formatDate(post.created_at)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {post.read_time}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="prose-custom" data-testid="blog-post-content">
          {renderContent(post.content)}
        </div>

        {/* Back */}
        <div className="mt-12 pt-6 border-t border-[#E5E7EB]">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center text-sm font-medium text-[#06B6D4] hover:text-[#0596B5] transition-colors"
            data-testid="blog-post-back-bottom"
          >
            <ArrowLeft className="mr-1.5 w-4 h-4" /> Back to all articles
          </button>
        </div>
      </article>

      <Footer />
    </div>
  );
}
